import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { PURGE } from "redux-persist"
import { RootState } from "./store"
import { RouteStop } from "./routes2gSlice"
import { DeliveryOrderMetadata } from "@/api/adHocCommands"

// Assignment lifecycle: unassigned -> dispatcher -> vehicle.
// Assignment to a vehicle implies a dispatcher (every vehicle belongs to one).
export type PlannedOrderAssignmentStatus =
  | "unassigned"
  | "dispatcher"
  | "vehicle"

export type PlannedOrder = {
  id: string
  stops: RouteStop[]
  metadata?: DeliveryOrderMetadata
  assignmentStatus: PlannedOrderAssignmentStatus
  // Bare JID of the office user the order is assigned to
  assignedDispatcherJid?: string
  // MUC/chat JID of the vehicle the order is assigned to
  assignedVehicleChatJid?: string
  createdBy?: string
  createdAt: string
  updatedAt?: string
  // Present only for orders pulled from Navigator; carried through so
  // assignment write-back (gps) knows which Navigator transport to PATCH.
  navigatorTransportId?: string
}

type PlannedOrdersState = {
  orders: PlannedOrder[]
}

const initialState: PlannedOrdersState = {
  orders: [],
}

const plannedOrders = createSlice({
  name: "plannedOrders",
  initialState,
  reducers: {
    upsertPlannedOrder: (
      state,
      action: PayloadAction<{ order: PlannedOrder }>,
    ) => {
      const index = state.orders.findIndex(
        (o) => o.id === action.payload.order.id,
      )
      if (index === -1) {
        state.orders.push(action.payload.order)
      } else {
        state.orders[index] = action.payload.order
      }
    },
    // Server-wins replacement used after fetching from the gps backend
    setPlannedOrders: (
      state,
      action: PayloadAction<{ orders: PlannedOrder[] }>,
    ) => {
      state.orders = action.payload.orders
    },
    batchUpsertPlannedOrders: (
      state,
      action: PayloadAction<{ orders: PlannedOrder[] }>,
    ) => {
      for (const order of action.payload.orders) {
        const index = state.orders.findIndex((o) => o.id === order.id)
        if (index === -1) {
          state.orders.push(order)
        } else {
          state.orders[index] = order
        }
      }
    },
    assignPlannedOrderToDispatcher: (
      state,
      action: PayloadAction<{ id: string; dispatcherJid: string }>,
    ) => {
      const order = state.orders.find((o) => o.id === action.payload.id)
      if (!order) return
      order.assignmentStatus = "dispatcher"
      order.assignedDispatcherJid = action.payload.dispatcherJid
      order.assignedVehicleChatJid = undefined
      order.updatedAt = new Date().toISOString()
    },
    assignPlannedOrderToVehicle: (
      state,
      action: PayloadAction<{
        id: string
        vehicleChatJid: string
        dispatcherJid?: string
      }>,
    ) => {
      const order = state.orders.find((o) => o.id === action.payload.id)
      if (!order) return
      order.assignmentStatus = "vehicle"
      order.assignedVehicleChatJid = action.payload.vehicleChatJid
      if (action.payload.dispatcherJid) {
        order.assignedDispatcherJid = action.payload.dispatcherJid
      }
      order.updatedAt = new Date().toISOString()
    },
    unassignPlannedOrder: (state, action: PayloadAction<{ id: string }>) => {
      const order = state.orders.find((o) => o.id === action.payload.id)
      if (!order) return
      order.assignmentStatus = "unassigned"
      order.assignedDispatcherJid = undefined
      order.assignedVehicleChatJid = undefined
      order.updatedAt = new Date().toISOString()
    },
    removePlannedOrder: (state, action: PayloadAction<{ id: string }>) => {
      state.orders = state.orders.filter((o) => o.id !== action.payload.id)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState)
  },
})

export const selectPlannedOrders = (state: RootState) =>
  state.plannedOrders.orders

export const unassignedOrdersOf = (orders: PlannedOrder[]): PlannedOrder[] =>
  orders.filter((o) => o.assignmentStatus === "unassigned")

export const findPlannedOrdersForDispatcher = (
  dispatcherJid: string | undefined,
) =>
  createSelector([selectPlannedOrders], (orders) =>
    orders.filter(
      (o) =>
        o.assignmentStatus === "dispatcher" &&
        o.assignedDispatcherJid === dispatcherJid,
    ),
  )

export const findPlannedOrdersForVehicle = (
  vehicleChatJid: string | undefined,
) =>
  createSelector([selectPlannedOrders], (orders) =>
    orders.filter(
      (o) =>
        o.assignmentStatus === "vehicle" &&
        o.assignedVehicleChatJid === vehicleChatJid,
    ),
  )

// Pkt 3 of the planner scope: simple per-dispatcher assigned-order counts
// (the spreadsheet equivalent), covers both dispatcher- and vehicle-assigned.
export const assignedCountByDispatcherOf = (
  orders: PlannedOrder[],
): Record<string, number> => {
  const counts: Record<string, number> = {}
  for (const order of orders) {
    if (!order.assignedDispatcherJid) continue
    counts[order.assignedDispatcherJid] =
      (counts[order.assignedDispatcherJid] ?? 0) + 1
  }
  return counts
}

export const firstPickupStop = (order: PlannedOrder): RouteStop | undefined =>
  order.stops.find((stop) => stop.action === "pickup") ?? order.stops[0]

export const lastDeliveryStop = (order: PlannedOrder): RouteStop | undefined =>
  [...order.stops].reverse().find((stop) => stop.action === "delivery") ??
  order.stops[order.stops.length - 1]

export const {
  upsertPlannedOrder,
  setPlannedOrders,
  batchUpsertPlannedOrders,
  assignPlannedOrderToDispatcher,
  assignPlannedOrderToVehicle,
  unassignPlannedOrder,
  removePlannedOrder,
} = plannedOrders.actions

export default plannedOrders.reducer
